import { z } from 'zod'

/**
 * Order Review contracts (Screen 2). Pinned to the backend's
 * GET /api/queue/{shopifyOrderId} and POST /api/dropship shapes — the backend
 * is being built to these exact wire formats in parallel.
 *
 * All money is a decimal STRING on the wire (unit_price, thresholds, totals);
 * keep it a string here and never parse to float for display. review-logic.ts
 * does the cents math.
 */

export const stockStateSchema = z.enum(['IN_STOCK', 'OOS_INCOMING', 'OOS_NO_INCOMING'])
export type StockState = z.infer<typeof stockStateSchema>

export const form5106StatusSchema = z.enum(['PENDING', 'ON_FILE'])
export type Form5106Status = z.infer<typeof form5106StatusSchema>

export const shippingAddressSchema = z.object({
  name: z.string(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  province_code: z.string(),
  zip: z.string(),
  country_code: z.string(),
})
export type ShippingAddress = z.infer<typeof shippingAddressSchema>

export const vipMetricsSchema = z.object({
  order_count: z.number(),
  aov: z.string(),
})
export type VipMetrics = z.infer<typeof vipMetricsSchema>

export const thresholdsSchema = z.object({
  min_threshold: z.string(),
  override_floor: z.string(),
  dropship_fee: z.string(),
})
export type Thresholds = z.infer<typeof thresholdsSchema>

export const lineItemSchema = z.object({
  line_item_id: z.string(),
  title: z.string(),
  sku: z.string(),
  ean: z.string().nullable(),
  vendor: z.string(),
  quantity: z.number(),
  unit_price: z.string(),
  stock_state: stockStateSchema,
  available: z.number(),
  incoming: z.number(),
  pre_checked: z.boolean(),
})
export type LineItem = z.infer<typeof lineItemSchema>

export const activeDropshipRefSchema = z.object({
  dropship_order_id: z.number(),
  reference_client: z.string(),
  status: z.string(),
  submitted_at: z.string().nullable(),
  submitted_by: z.string().nullable(),
})
export type ActiveDropshipRef = z.infer<typeof activeDropshipRefSchema>

export const orderReviewSchema = z.object({
  shopify_order_id: z.string(),
  order_number: z.string(),
  customer_name: z.string(),
  customer_email: z.string().nullable(),
  customer_phone: z.string().nullable(),
  shipping_address: shippingAddressSchema,
  is_vip: z.boolean(),
  vip_metrics: vipMetricsSchema.nullable(),
  order_date: z.string(),
  // Shopify order note (display-only); distinct from our internal note.
  order_note: z.string().nullable(),
  form_5106_preview: form5106StatusSchema,
  thresholds: thresholdsSchema,
  line_items: z.array(lineItemSchema),
  // Set when this order already has a dropship submitted to KH. Defaults tolerate
  // a not-yet-updated backend that omits these fields.
  already_submitted: z.boolean().default(false),
  active_dropship: activeDropshipRefSchema.nullable().optional().default(null),
})
export type OrderReview = z.infer<typeof orderReviewSchema>

// ---- POST /api/dropship ----

export const dropshipRequestSchema = z.object({
  shopify_order_id: z.string(),
  line_item_ids: z.array(z.string()),
  override: z
    .object({
      enabled: z.boolean(),
      note: z.string(),
    })
    .nullable(),
  internal_note: z.string().nullable(),
})
export type DropshipRequest = z.infer<typeof dropshipRequestSchema>

export const dropshipResponseSchema = z.object({
  dropship_order_id: z.number(),
  reference_client: z.string(),
  kh_order_id: z.string().nullable(),
  kh_status: z.string().nullable(),
  status: z.string(),
  form_5106_status: form5106StatusSchema,
  selected_total: z.string(),
  dropship_fee: z.string(),
})
export type DropshipResponse = z.infer<typeof dropshipResponseSchema>
